import { useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Caption1,
  Card,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from 'recharts';
import { useSharePointList } from '@/hooks/useSharePointList';
import { canViewCosts } from '@/utils/permissions';
import { AccessDenied } from '@/components/AccessDenied';
import type {
  Employee,
  Application,
  AppAssignment,
  LicenseSync,
} from '@/models/types';
import { useAppContext } from '@/store/appContext';
import { formatCurrency } from '@/utils/formatters';
import { DataTable, type Column } from '@/components/DataTable';

// Brand palette — pulled from prismTheme so charts feel native to Prism
// instead of stock Microsoft chart colours.
const COLORS = [
  '#5B92E5', // brand blue
  '#7DB9B3', // teal
  '#D8A878', // orange
  '#A855F7', // purple
  '#22C55E', // green
  '#F59E0B', // amber
  '#06B6D4', // cyan
  '#EF4444', // red
];

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '24px' },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    padding: '20px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
  },
  cardValue: {
    fontSize: '32px',
    fontWeight: 700,
    color: tokens.colorBrandForeground1,
    marginTop: '8px',
    letterSpacing: '-0.5px',
    fontFamily: '"SF Mono", ui-monospace, "JetBrains Mono", Menlo, monospace',
    fontVariantNumeric: 'tabular-nums',
  },
  charts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '16px',
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
    },
  },
  chartCard: {
    padding: '20px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    minHeight: '340px',
  },
  chartTitle: {
    fontWeight: 600,
    marginBottom: '16px',
  },
});

interface EmployeeCostRow {
  id: string;
  name: string;
  department: string;
  appCount: number;
  monthlyCost: number;
}

export function CostCenter() {
  const styles = useStyles();
  const { listIds, currentUser } = useAppContext();

  // Security: Cost data is sensitive — only Admin/RO can view
  if (currentUser && !canViewCosts(currentUser.role)) {
    return <AccessDenied reason="Cost data is restricted to Admin and Reporting Officers." />;
  }

  const { items: employees } = useSharePointList<Employee>('OC_Employees', {
    autoFetch: !!listIds,
  });
  const { items: apps } = useSharePointList<Application>('OC_Applications', {
    autoFetch: !!listIds,
  });
  const { items: assignments } = useSharePointList<AppAssignment>(
    'OC_AppAssignments',
    { autoFetch: !!listIds },
  );
  const { items: licenseSyncData } = useSharePointList<LicenseSync>(
    'OC_LicenseSync',
    { autoFetch: !!listIds },
  );

  // Build app cost map: appId → monthly cost
  const appCostMap = useMemo(() => {
    const map = new Map<string, { cost: number; category: string; title: string }>();
    for (const app of apps) {
      map.set(app.id, {
        cost: app.fields.MonthlyCostPerLicense,
        category: app.fields.Category,
        title: app.fields.Title,
      });
    }
    return map;
  }, [apps]);

  // Per-employee costs
  const employeeCosts = useMemo((): EmployeeCostRow[] => {
    const activeAssignments = assignments.filter(
      (a) => a.fields.Status === 'Active',
    );
    const empMap = new Map<
      string,
      { name: string; department: string; cost: number; count: number }
    >();

    for (const emp of employees) {
      if (emp.fields.Status === 'Active') {
        empMap.set(emp.id, {
          name: emp.fields.Title,
          department: emp.fields.Department,
          cost: 0,
          count: 0,
        });
      }
    }

    for (const a of activeAssignments) {
      const empId = String(a.fields.EmployeeId);
      const appInfo = appCostMap.get(String(a.fields.AppId));
      const emp = empMap.get(empId);
      if (emp && appInfo) {
        emp.cost += appInfo.cost;
        emp.count++;
      }
    }

    return Array.from(empMap.entries()).map(([id, data]) => ({
      id,
      name: data.name,
      department: data.department,
      appCount: data.count,
      monthlyCost: data.cost,
    }));
  }, [employees, assignments, appCostMap]);

  // Summary stats
  const totalMonthlyCost = useMemo(
    () => employeeCosts.reduce((sum, e) => sum + e.monthlyCost, 0),
    [employeeCosts],
  );

  const avgCostPerEmployee = useMemo(
    () =>
      employeeCosts.length > 0
        ? totalMonthlyCost / employeeCosts.length
        : 0,
    [totalMonthlyCost, employeeCosts],
  );

  // Cost by department
  const costByDepartment = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of employeeCosts) {
      const dept = e.department || 'Unknown';
      map.set(dept, (map.get(dept) ?? 0) + e.monthlyCost);
    }
    return Array.from(map.entries())
      .map(([name, cost]) => ({ name, cost: Math.round(cost * 100) / 100 }))
      .sort((a, b) => b.cost - a.cost);
  }, [employeeCosts]);

  // Cost by app category
  const costByCategory = useMemo(() => {
    const map = new Map<string, number>();
    const activeAssignments = assignments.filter(
      (a) => a.fields.Status === 'Active',
    );

    for (const a of activeAssignments) {
      const appInfo = appCostMap.get(String(a.fields.AppId));
      if (appInfo) {
        map.set(
          appInfo.category,
          (map.get(appInfo.category) ?? 0) + appInfo.cost,
        );
      }
    }

    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [assignments, appCostMap]);

  // Top apps by cost (total across all assignments)
  const topAppsByCost = useMemo(() => {
    const map = new Map<string, number>();
    const activeAssignments = assignments.filter(
      (a) => a.fields.Status === 'Active',
    );

    for (const a of activeAssignments) {
      const appInfo = appCostMap.get(String(a.fields.AppId));
      if (appInfo) {
        map.set(appInfo.title, (map.get(appInfo.title) ?? 0) + appInfo.cost);
      }
    }

    return Array.from(map.entries())
      .map(([name, cost]) => ({ name: name.length > 25 ? name.slice(0, 23) + '...' : name, cost }))
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 10);
  }, [assignments, appCostMap]);

  // License sync trend (group by month)
  const syncTrend = useMemo(() => {
    const map = new Map<string, number>();
    for (const l of licenseSyncData) {
      const month = l.fields.SyncTimestamp?.slice(0, 7) ?? 'Unknown';
      map.set(month, (map.get(month) ?? 0) + l.fields.MonthlyCost);
    }
    return Array.from(map.entries())
      .map(([month, cost]) => ({ month, cost }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [licenseSyncData]);

  const highestDept = costByDepartment[0];

  // Per-employee table columns
  const empCostColumns: Column<EmployeeCostRow>[] = [
    {
      key: 'name',
      label: 'Employee',
      sortable: true,
      getValue: (item) => item.name,
    },
    {
      key: 'department',
      label: 'Department',
      sortable: true,
      getValue: (item) => item.department,
    },
    {
      key: 'appCount',
      label: 'Apps',
      sortable: true,
      getValue: (item) => item.appCount,
    },
    {
      key: 'monthlyCost',
      label: 'Monthly Cost',
      sortable: true,
      getValue: (item) => item.monthlyCost,
      render: (item) => <Body1>{formatCurrency(item.monthlyCost)}</Body1>,
    },
  ];

  return (
    <div className={styles.page}>
      <PageTitle>Cost Center</PageTitle>

      {/* Summary Cards */}
      <div className={styles.cards}>
        <div className={styles.card}>
          <Caption1>Total Monthly Cost</Caption1>
          <div className={styles.cardValue}>
            {formatCurrency(totalMonthlyCost)}
          </div>
        </div>
        <div className={styles.card}>
          <Caption1>Avg Cost / Employee</Caption1>
          <div className={styles.cardValue}>
            {formatCurrency(avgCostPerEmployee)}
          </div>
        </div>
        <div className={styles.card}>
          <Caption1>Highest Department</Caption1>
          <div className={styles.cardValue}>
            {highestDept ? highestDept.name : '—'}
          </div>
          {highestDept && (
            <Caption1>{formatCurrency(highestDept.cost)}/mo</Caption1>
          )}
        </div>
        <div className={styles.card}>
          <Caption1>Total Applications</Caption1>
          <div className={styles.cardValue}>{apps.length}</div>
        </div>
      </div>

      {/* Charts */}
      <div className={styles.charts}>
        <Card className={styles.chartCard}>
          <Body1 className={styles.chartTitle}>Cost by Department</Body1>
          {costByDepartment.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={costByDepartment}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Bar dataKey="cost" fill="#0078d4" name="Monthly Cost" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
              No cost data available yet.
            </Body1>
          )}
        </Card>

        <Card className={styles.chartCard}>
          <Body1 className={styles.chartTitle}>Cost by Category</Body1>
          {costByCategory.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={costByCategory}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  dataKey="value"
                  label={({ name }) => name}
                >
                  {costByCategory.map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
              No cost data available yet.
            </Body1>
          )}
        </Card>

        <Card className={styles.chartCard}>
          <Body1 className={styles.chartTitle}>Top 10 Apps by Cost</Body1>
          {topAppsByCost.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topAppsByCost} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={160}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Bar dataKey="cost" fill="#8764b8" name="Total Cost" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
              No cost data available yet.
            </Body1>
          )}
        </Card>

        {syncTrend.length > 0 && (
          <Card className={styles.chartCard}>
            <Body1 className={styles.chartTitle}>Cost Trend</Body1>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={syncTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Line
                  type="monotone"
                  dataKey="cost"
                  stroke="#0078d4"
                  strokeWidth={2}
                  name="Monthly Cost"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* Per-employee cost breakdown */}
      <Body1 style={{ fontWeight: 600, fontSize: 18 }}>
        Per-Employee Cost Breakdown
      </Body1>
      <DataTable
        columns={empCostColumns}
        items={employeeCosts}
        searchPlaceholder="Search employees..."
        searchFields={(item) => `${item.name} ${item.department}`}
        getRowKey={(item) => item.id}
        emptyMessage="No cost data. Assign apps to members to see costs."
      />
    </div>
  );
}
